import { useTranslation } from "react-i18next";
import {
  CloseOutlined,
  DeleteOutlined,
  QuestionCircleFilled,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Popover } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { useCheckPermissions } from "hooks/useCheckPermissions";

const NOTE_MAX_LENGTH = 150;


const PopConfirmationUnavailable = ({ adminName, isLoading, onSubmit }) => {
  const { t } = useTranslation();
  const hasAccess = useCheckPermissions("meeting_schedule_other_admin_access");

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [forAllAdmin, setForAllAdmin] = useState(false);
  const [note, setNote] = useState("");
  const noteInputRef = useRef(null);

  const targetName = useMemo(
    () => (forAllAdmin ? t("meeting_schedule.all_admins") : adminName ?? ""),
    [forAllAdmin, adminName, t]
  );

  const openModalFor = useCallback((forAll) => {
    setForAllAdmin(forAll);
    setNote("");
    setPopoverOpen(false);
    setModalOpen(true);
  }, []);

  const handleClickButton = useCallback(() => {
    if (hasAccess) {
      setPopoverOpen(true);
    } else {
      openModalFor(false);
    }
  }, [hasAccess, openModalFor]);

  const handleCancel = useCallback(() => {
    if (isLoading) return;
    setModalOpen(false);
  }, [isLoading]);

  const handleSubmit = useCallback(() => {
    const trimmed = note.trim();
    setNote(trimmed);
    onSubmit(false, forAllAdmin, () => setModalOpen(false), trimmed || null);
  }, [onSubmit, forAllAdmin, note]);

  const popoverContent = useMemo(
    () => (
      <div className="relative">
        <div className="font-bold flex gap-x-2 ">
          <QuestionCircleFilled className="text-green-500" />
          {t("meeting_management.set_unavailable")}
        </div>
        <p>
          {t("meeting_schedule.set_unavailable_desc1", { value: adminName })}
        </p>
        <div className="flex justify-center gap-x-2 mt-3">
          <Button
            size="small"
            icon={<TeamOutlined />}
            onClick={() => openModalFor(true)}
          >
            {t("meeting_schedule.all_admins")}
          </Button>
          <Button
            size="small"
            icon={<UserOutlined />}
            type="primary"
            onClick={() => openModalFor(false)}
          >
            {adminName}
          </Button>
        </div>
        <div
          className="absolute top-1 right-1 p-1 px-2 hover:bg-[#dedede]/50 rounded-md cursor-pointer"
          onClick={() => setPopoverOpen(false)}
        >
          <CloseOutlined />
        </div>
      </div>
    ),
    [t, adminName, openModalFor]
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        open={hasAccess && popoverOpen}
        onOpenChange={(o) => setPopoverOpen(o)}
      >
        <Button
          type="primary"
          icon={<DeleteOutlined />}
          className="bg-[#D2D2D2] text-black hover:bg-[#dedede] active:bg-[#ababab]"
          onClick={handleClickButton}
        >
          {t("meeting_management.set_unavailable")}
        </Button>
      </Popover>

      <Modal
        title={t("meeting_management.set_unavailable_modal_title", {
          value: targetName,
        })}
        open={modalOpen}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText={t("button.save")}
        cancelText={t("button.cancel")}
        confirmLoading={isLoading}
        cancelButtonProps={{ disabled: isLoading }}
        maskClosable={!isLoading}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) noteInputRef.current?.focus();
        }}
      >
        <Input
          ref={noteInputRef}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onPressEnter={handleSubmit}
          placeholder={t(
            "meeting_management.set_unavailable_note_placeholder"
          )}
          maxLength={NOTE_MAX_LENGTH}
          showCount
          disabled={isLoading}
        />
      </Modal>
    </>
  );
};

export default PopConfirmationUnavailable;
